import { useState, useEffect } from "react";
import { useAuth, useDateFilter, API } from "../App";
import Layout from "../components/Layout";
import DateRangePicker from "../components/DateRangePicker";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { Plus, Trash2, Pencil, DollarSign, Building2, CheckCircle2, Filter, CheckSquare, Square, Settings, Landmark } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Checkbox } from "../components/ui/checkbox";

const AccountExpensesPage = () => {
  const { token } = useAuth();
  const { startDate, endDate } = useDateFilter();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [banks, setBanks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBusiness, setFilterBusiness] = useState("all");
  const [filterPaid, setFilterPaid] = useState("all");
  const [filterBank, setFilterBank] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Bank management state
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankForm, setBankForm] = useState({ name: "", color: "#3B82F6" });

  const [form, setForm] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    description: "",
    amount: "",
    category_id: "",
    payment_method_id: "",
    bank_id: "",
    business: "heladeria",
    is_paid: false,
    notes: ""
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(endDate, "yyyy-MM-dd");

      const [expensesRes, summaryRes, categoriesRes, paymentMethodsRes, banksRes] = await Promise.all([
        axios.get(`${API}/account/expenses?start_date=${startStr}&end_date=${endStr}`, getAuthHeaders()),
        axios.get(`${API}/account/expenses/summary?start_date=${startStr}&end_date=${endStr}`, getAuthHeaders()),
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/payment-methods`, getAuthHeaders()),
        axios.get(`${API}/banks`, getAuthHeaders())
      ]);

      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
      setCategories(categoriesRes.data);
      setPaymentMethods(paymentMethodsRes.data);
      setBanks(banksRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.description || !form.amount || !form.category_id) {
      toast.error("Por favor completa los campos requeridos");
      return;
    }

    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount)
      };

      if (editingExpense) {
        await axios.put(`${API}/account/expenses/${editingExpense.id}`, payload, getAuthHeaders());
        toast.success("Gasto actualizado");
      } else {
        await axios.post(`${API}/account/expenses`, payload, getAuthHeaders());
        toast.success("Gasto registrado");
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar gasto");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;

    try {
      await axios.delete(`${API}/account/expenses/${id}`, getAuthHeaders());
      toast.success("Gasto eliminado");
      setSelectedIds(prev => prev.filter(i => i !== id));
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar gasto");
    }
  };

  // Bulk selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredExpenses.map(exp => exp.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(i => i !== id));
    }
  };

  const handleBulkUpdatePaid = async (isPaid) => {
    if (selectedIds.length === 0) return;
    
    setBulkUpdating(true);
    try {
      await axios.post(`${API}/account/expenses/bulk-update-paid`, {
        expense_ids: selectedIds,
        is_paid: isPaid
      }, getAuthHeaders());
      
      toast.success(`${selectedIds.length} gastos actualizados a "${isPaid ? 'Pagado' : 'Pendiente'}"`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar gastos");
    } finally {
      setBulkUpdating(false);
    }
  };

  const filteredExpenses = expenses.filter(exp => {
    const matchesSearch = exp.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exp.category_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBusiness = filterBusiness === "all" || exp.business === filterBusiness;
    const matchesPaid = filterPaid === "all" || 
      (filterPaid === "yes" && exp.is_paid) || 
      (filterPaid === "no" && !exp.is_paid);
    const matchesBank = filterBank === "all" || 
      (filterBank === "none" && !exp.bank_id) ||
      exp.bank_id === filterBank;
    return matchesSearch && matchesBusiness && matchesPaid && matchesBank;
  });

  const isAllSelected = filteredExpenses.length > 0 && selectedIds.length === filteredExpenses.length;
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < filteredExpenses.length;

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setForm({
      date: expense.date,
      description: expense.description,
      amount: expense.amount.toString(),
      category_id: expense.category_id,
      payment_method_id: expense.payment_method_id || "",
      bank_id: expense.bank_id || "",
      business: expense.business,
      is_paid: expense.is_paid,
      notes: expense.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setForm({
      date: format(new Date(), "yyyy-MM-dd"),
      description: "",
      amount: "",
      category_id: "",
      payment_method_id: "",
      bank_id: "",
      business: "heladeria",
      is_paid: false,
      notes: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const businessLabels = {
    heladeria: "Heladería",
    merch: "Merch",
    personal: "Personal"
  };

  const businessColors = {
    heladeria: "bg-green-100 text-green-800",
    merch: "bg-amber-100 text-amber-800",
    personal: "bg-purple-100 text-purple-800"
  };

  // Bank CRUD functions
  const handleBankSubmit = async (e) => {
    e.preventDefault();
    if (!bankForm.name.trim()) {
      toast.error("El nombre del banco es requerido");
      return;
    }
    
    try {
      if (editingBank) {
        await axios.put(`${API}/banks/${editingBank.id}`, bankForm, getAuthHeaders());
        toast.success("Banco actualizado");
      } else {
        await axios.post(`${API}/banks`, bankForm, getAuthHeaders());
        toast.success("Banco creado");
      }
      setBankDialogOpen(false);
      resetBankForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar banco");
    }
  };

  const handleEditBank = (bank) => {
    setEditingBank(bank);
    setBankForm({ name: bank.name, color: bank.color });
    setBankDialogOpen(true);
  };

  const handleDeleteBank = async (bankId) => {
    if (!window.confirm("¿Eliminar este banco?")) return;
    
    try {
      await axios.delete(`${API}/banks/${bankId}`, getAuthHeaders());
      toast.success("Banco eliminado");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar banco");
    }
  };

  const resetBankForm = () => {
    setEditingBank(null);
    setBankForm({ name: "", color: "#3B82F6" });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-medium">{payload[0].name}</p>
          <p className="text-[#F43F5E]">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner w-8 h-8"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="account-expenses-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Gastos de Cuenta</h1>
            <p className="text-[#6B7280]">Control de gastos por negocio y estado de pago</p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangePicker />
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#F43F5E] hover:bg-[#E11D48] text-white" data-testid="new-expense-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nuevo Gasto
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-[#4A4A4A]">
                    {editingExpense ? "Editar Gasto" : "Nuevo Gasto"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fecha</Label>
                      <Input 
                        type="date"
                        value={form.date}
                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                        className="bg-white border-[#FECDD3]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Monto *</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        placeholder="0.00"
                        className="bg-white border-[#FECDD3]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Descripción *</Label>
                    <Input 
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Descripción del gasto"
                      className="bg-white border-[#FECDD3]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Categoría *</Label>
                      <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                        <SelectTrigger className="bg-white border-[#FECDD3]">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }}></div>
                                {cat.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Método de Pago</Label>
                      <Select value={form.payment_method_id} onValueChange={(v) => setForm({ ...form, payment_method_id: v })}>
                        <SelectTrigger className="bg-white border-[#FECDD3]">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {paymentMethods.map(pm => (
                            <SelectItem key={pm.id} value={pm.id}>{pm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Banco</Label>
                    <Select value={form.bank_id || "none"} onValueChange={(v) => setForm({ ...form, bank_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="bg-white border-[#FECDD3]">
                        <SelectValue placeholder="Seleccionar banco" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin banco</SelectItem>
                        {banks.map(bank => (
                          <SelectItem key={bank.id} value={bank.id}>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: bank.color }}></div>
                              {bank.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Negocio *</Label>
                    <Select value={form.business} onValueChange={(v) => setForm({ ...form, business: v })}>
                      <SelectTrigger className="bg-white border-[#FECDD3]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="heladeria">Heladería</SelectItem>
                        <SelectItem value="merch">Merch</SelectItem>
                        <SelectItem value="personal">Personal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <Label className="font-medium">¿Pagado?</Label>
                    <Switch 
                      checked={form.is_paid}
                      onCheckedChange={(checked) => setForm({ ...form, is_paid: checked })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Notas</Label>
                    <Textarea 
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Notas adicionales..."
                      className="bg-white border-[#FECDD3]"
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#F43F5E] hover:bg-[#E11D48] text-white">
                    {editingExpense ? "Actualizar" : "Guardar"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6B7280]">Total Gastos</p>
                  <p className="text-2xl font-bold text-[#F43F5E]">{formatCurrency(summary?.total)}</p>
                  <p className="text-xs text-[#9CA3AF]">{summary?.count || 0} registros</p>
                </div>
                <DollarSign className="w-10 h-10 text-[#FECDD3]" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6B7280]">Pagado</p>
                  <p className="text-2xl font-bold text-[#10B981]">
                    {formatCurrency(summary?.by_payment_status?.find(s => s.name === "Pagado")?.value)}
                  </p>
                </div>
                <CheckCircle2 className="w-10 h-10 text-[#10B981]/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6B7280]">Pendiente</p>
                  <p className="text-2xl font-bold text-[#EF4444]">
                    {formatCurrency(summary?.by_payment_status?.find(s => s.name === "Pendiente")?.value)}
                  </p>
                </div>
                <Building2 className="w-10 h-10 text-[#EF4444]/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending by Business */}
        <Card className="bg-white border-[#FECDD3] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-[#4A4A4A] text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#EF4444]" />
              Pendiente por Negocio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {summary?.pending_by_business?.map((item) => (
                <div 
                  key={item.key} 
                  className="p-4 rounded-lg border-2"
                  style={{ borderColor: item.color, backgroundColor: `${item.color}10` }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium" style={{ color: item.color }}>{item.name}</p>
                      <p className="text-xl font-bold text-[#EF4444]">
                        {formatCurrency(item.value)}
                      </p>
                    </div>
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${item.color}20` }}
                    >
                      {item.key === 'heladeria' && <span className="text-lg">🍦</span>}
                      {item.key === 'merch' && <span className="text-lg">👕</span>}
                      {item.key === 'personal' && <span className="text-lg">👤</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {(!summary?.pending_by_business || summary.pending_by_business.every(b => b.value === 0)) && (
              <div className="text-center py-4 text-[#9CA3AF]">
                No hay gastos pendientes
              </div>
            )}
          </CardContent>
        </Card>

        {/* Charts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* By Business */}
          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardHeader>
              <CardTitle className="text-[#4A4A4A]">Por Negocio</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.by_business?.some(b => b.value > 0) ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={summary.by_business.filter(b => b.value > 0)}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        innerRadius={40}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {summary.by_business.filter(b => b.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-[#9CA3AF]">
                  No hay datos
                </div>
              )}
            </CardContent>
          </Card>

          {/* By Payment Status */}
          <Card className="bg-white border-[#FECDD3] shadow-sm">
            <CardHeader>
              <CardTitle className="text-[#4A4A4A]">Estado de Pago</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.by_payment_status?.some(s => s.value > 0) ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={summary.by_payment_status.filter(s => s.value > 0)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#FEE2E2" />
                      <XAxis type="number" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" width={80} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {summary.by_payment_status.filter(s => s.value > 0).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-[#9CA3AF]">
                  No hay datos
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Banks Configuration Panel */}
        <Card className="bg-white border-[#3B82F6]/30 shadow-sm" data-testid="banks-config-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[#4A4A4A] text-lg flex items-center gap-2">
                <Landmark className="w-5 h-5 text-[#3B82F6]" />
                Configuración de Bancos
              </CardTitle>
              <Dialog open={bankDialogOpen} onOpenChange={(open) => { setBankDialogOpen(open); if (!open) resetBankForm(); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="add-bank-btn">
                    <Plus className="w-4 h-4 mr-1" /> Agregar Banco
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="text-[#4A4A4A]">
                      {editingBank ? "Editar Banco" : "Nuevo Banco"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleBankSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Nombre del Banco *</Label>
                      <Input
                        value={bankForm.name}
                        onChange={(e) => setBankForm({ ...bankForm, name: e.target.value })}
                        placeholder="Ej: BBVA, Santander, etc."
                        className="bg-white border-[#BFDBFE]"
                        data-testid="bank-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="color"
                          value={bankForm.color}
                          onChange={(e) => setBankForm({ ...bankForm, color: e.target.value })}
                          className="w-16 h-10 p-1 cursor-pointer"
                        />
                        <Input
                          value={bankForm.color}
                          onChange={(e) => setBankForm({ ...bankForm, color: e.target.value })}
                          placeholder="#3B82F6"
                          className="flex-1 bg-white border-[#BFDBFE]"
                        />
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                      {editingBank ? "Actualizar" : "Crear Banco"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {banks.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {banks.map(bank => (
                  <div 
                    key={bank.id}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border"
                    style={{ borderColor: bank.color, backgroundColor: `${bank.color}10` }}
                  >
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: bank.color }}></div>
                    <span className="font-medium text-[#4A4A4A]">{bank.name}</span>
                    <div className="flex gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditBank(bank)}
                        className="h-6 w-6 p-0 text-[#3B82F6] hover:bg-[#3B82F6]/10"
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBank(bank.id)}
                        className="h-6 w-6 p-0 text-[#EF4444] hover:bg-[#EF4444]/10"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#9CA3AF] text-center py-4">
                No hay bancos configurados. Agrega uno para comenzar.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Expenses List */}
        <Card className="bg-white border-[#FECDD3] shadow-sm">
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-center">
                <CardTitle className="text-[#4A4A4A]">Listado de Gastos</CardTitle>
                <Input 
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64 bg-white border-[#FECDD3]"
                  data-testid="search-input"
                />
              </div>
              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-[#6B7280]">
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">Filtros:</span>
                </div>
                <Select value={filterBusiness} onValueChange={setFilterBusiness}>
                  <SelectTrigger className="w-40 bg-white border-[#FECDD3]" data-testid="filter-business">
                    <SelectValue placeholder="Negocio" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los negocios</SelectItem>
                    <SelectItem value="heladeria">Heladería</SelectItem>
                    <SelectItem value="merch">Merch</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterPaid} onValueChange={setFilterPaid}>
                  <SelectTrigger className="w-40 bg-white border-[#FECDD3]" data-testid="filter-paid">
                    <SelectValue placeholder="¿Pagado?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="yes">Pagado</SelectItem>
                    <SelectItem value="no">Pendiente</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterBank} onValueChange={setFilterBank} data-testid="filter-bank">
                  <SelectTrigger className="w-[150px] bg-white border-[#FECDD3]">
                    <SelectValue placeholder="Banco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los bancos</SelectItem>
                    <SelectItem value="none">Sin banco</SelectItem>
                    {banks.map(bank => (
                      <SelectItem key={bank.id} value={bank.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: bank.color }}></div>
                          {bank.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(filterBusiness !== "all" || filterPaid !== "all" || filterBank !== "all") && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setFilterBusiness("all"); setFilterPaid("all"); setFilterBank("all"); }}
                    className="text-[#6B7280] hover:text-[#F43F5E]"
                    data-testid="clear-filters"
                  >
                    Limpiar filtros
                  </Button>
                )}
                <span className="ml-auto text-sm text-[#9CA3AF]">
                  {filteredExpenses.length} de {expenses.length} registros
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Bulk Actions Bar */}
            {selectedIds.length > 0 && (
              <div className="mb-4 p-3 bg-[#FEE2E2] rounded-lg flex items-center justify-between" data-testid="bulk-actions-bar">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-[#F43F5E]" />
                  <span className="text-[#4A4A4A] font-medium">
                    {selectedIds.length} {selectedIds.length === 1 ? 'gasto seleccionado' : 'gastos seleccionados'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleBulkUpdatePaid(true)}
                    disabled={bulkUpdating}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="bulk-mark-paid"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Marcar como Pagado
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleBulkUpdatePaid(false)}
                    disabled={bulkUpdating}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    data-testid="bulk-mark-pending"
                  >
                    <Square className="w-4 h-4 mr-1" />
                    Marcar como Pendiente
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedIds([])}
                    className="text-[#6B7280]"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
            
            {filteredExpenses.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#FECDD3]">
                      <th className="text-left py-3 px-2 w-10">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={handleSelectAll}
                          className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E] data-[state=checked]:border-[#F43F5E]"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="text-left py-3 px-2 text-[#6B7280] font-medium">Fecha</th>
                      <th className="text-left py-3 px-2 text-[#6B7280] font-medium">Descripción</th>
                      <th className="text-left py-3 px-2 text-[#6B7280] font-medium">Categoría</th>
                      <th className="text-left py-3 px-2 text-[#6B7280] font-medium">Banco</th>
                      <th className="text-left py-3 px-2 text-[#6B7280] font-medium">Negocio</th>
                      <th className="text-left py-3 px-2 text-[#6B7280] font-medium">¿Pagado?</th>
                      <th className="text-right py-3 px-2 text-[#6B7280] font-medium">Monto</th>
                      <th className="text-right py-3 px-2 text-[#6B7280] font-medium">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredExpenses.map(expense => (
                      <tr 
                        key={expense.id} 
                        className={`border-b border-[#FEE2E2] hover:bg-[#FFF5F5] ${selectedIds.includes(expense.id) ? 'bg-[#FEE2E2]' : ''}`}
                      >
                        <td className="py-3 px-2">
                          <Checkbox
                            checked={selectedIds.includes(expense.id)}
                            onCheckedChange={(checked) => handleSelectOne(expense.id, checked)}
                            className="border-[#FECDD3] data-[state=checked]:bg-[#F43F5E] data-[state=checked]:border-[#F43F5E]"
                            data-testid={`checkbox-${expense.id}`}
                          />
                        </td>
                        <td className="py-3 px-2 text-[#4A4A4A]">{expense.date}</td>
                        <td className="py-3 px-2 text-[#4A4A4A]">{expense.description}</td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: expense.category_color }}></div>
                            <span className="text-[#4A4A4A]">{expense.category_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-2">
                          {expense.bank_name ? (
                            <span className="text-[#4A4A4A]">{expense.bank_name}</span>
                          ) : (
                            <span className="text-[#9CA3AF]">-</span>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          <Badge className={businessColors[expense.business]}>
                            {businessLabels[expense.business]}
                          </Badge>
                        </td>
                        <td className="py-3 px-2">
                          <Badge className={expense.is_paid ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                            {expense.is_paid ? "Sí" : "No"}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-right text-[#F43F5E] font-medium">
                          {formatCurrency(expense.amount)}
                        </td>
                        <td className="py-3 px-2 text-right">
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEdit(expense)}
                              className="text-[#3B82F6] hover:bg-[#3B82F6]/10"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDelete(expense.id)}
                              className="text-[#EF4444] hover:bg-[#EF4444]/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center text-[#9CA3AF]">
                <DollarSign className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay gastos en este período</p>
                <p className="text-sm">Agrega un nuevo gasto para comenzar</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AccountExpensesPage;
